/*
 * Módulo de autenticación OIDC (Keycloak) para el NUEVO frontend system de Backstage.
 *
 * El nuevo frontend system no permite configurar el sign-in por YAML: hay que
 * (1) registrar una auth API para el provider 'oidc' (OAuth2 genérico) y
 * (2) sobrescribir el extension `sign-in-page` para renderizar un SignInPage
 *     que use esa API.
 *
 * El provider 'oidc' del frontend matchea el provider 'oidc' del backend
 * (packages/backend/src/modules/oidcResolver.ts).
 */
import {
  ApiBlueprint,
  createApiRef,
  configApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { OAuth2 } from '@backstage/core-app-api';
import { SignInPage } from '@backstage/core-components';
import {
  OAuthApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
} from '@backstage/core-plugin-api';

// API ref del provider OIDC. OAuth2 implementa todas estas interfaces.
export const oidcAuthApiRef = createApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
>({
  id: 'internal.auth.oidc',
});

// Factory de la auth API: OAuth2 genérico apuntando al provider 'oidc' del backend.
const oidcAuthApi = ApiBlueprint.make({
  name: 'oidc',
  params: defineParams =>
    defineParams({
      api: oidcAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          provider: {
            id: 'oidc',
            title: 'Keycloak Solop',
            icon: () => null,
          },
          defaultScopes: ['openid', 'profile', 'email'],
          environment: configApi.getOptionalString('auth.environment'),
        }),
    }),
});

// Pantalla de login: SOLO Keycloak (SSO). Un único provider → una sola tarjeta.
const SolopSignInPage = (props: {
  onSignInSuccess: (identityApi: any) => void;
}) => (
  <SignInPage
    {...props}
    title="Solop Portal"
    provider={{
      id: 'oidc',
      title: 'Keycloak Solop',
      message: 'Iniciá sesión con tu cuenta Solop',
      apiRef: oidcAuthApiRef,
    }}
  />
);

const oidcSignInPage = SignInPageBlueprint.make({
  params: {
    loader: () => Promise.resolve(SolopSignInPage),
  },
});

export const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [oidcAuthApi, oidcSignInPage],
});
